// Single sink for the app's uncaught errors (React boundaries + global events).
//
// It only writes to the console today: there is no error-telemetry backend in this project and
// adding one is a product call (cost, privacy, GDPR). The value of funnelling everything here is
// that the day one exists (Sentry, Cloud Logging, whatever) it gets wired in ONE place and every
// call site is already covered.
import { DEBUG } from '@/config/settings'

/** Where the error came from: the React boundary that caught it, or the global event. */
export type ErrorScope = 'app' | 'map' | 'window' | 'promise'

/** Free-form context that travels with the error (componentStack, url, …). */
export type ErrorContext = Record<string, unknown>

/**
 * Records an uncaught error. NEVER throws: if reporting itself failed, the original failure is bad
 * enough, and an exception here would also break the fallback that is rendering because of it.
 */
export function reportError(
  scope: ErrorScope,
  error: unknown,
  context: ErrorContext = {},
): void {
  try {
    const normalized =
      error instanceof Error
        ? error
        : new Error(String(error), { cause: error })

    // eslint-disable-next-line no-console
    console.error(`[error:${scope}] ${normalized.message}`, {
      ...context,
      // Full stack only in debug mode: in production it clutters the user's console and helps them
      // not at all, while the message + scope do help describe the incident.
      ...(DEBUG ? { stack: normalized.stack } : {}),
    })
  } catch {
    // no sink available: dropped on purpose
  }
}
